import { Box, Button, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import React from 'react';
import type { Fund } from '../../data/types';
import { date, maskAccount } from '../../lib/format';
import { BASIS_LABELS } from '../../lib/labels';
import { useToast } from '../../ui/feedback';
import { KeyValueGrid, SectionCard } from '../../ui/primitives';

export default function FundSettingsTab({ fund }: { fund: Fund }) {
  const toast = useToast();
  const comingSoon = () =>
    toast(
      'Editing fund settings creates a change for approval (not wired in the prototype)',
      'info',
    );
  return (
    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' } }}>
      <SectionCard
        title="Trust details"
        action={
          <Button size="small" onClick={comingSoon}>
            Edit
          </Button>
        }
      >
        <KeyValueGrid
          items={[
            { label: 'Trustee / responsible entity', value: fund.trustee },
            { label: 'Trust ABN', value: fund.abn },
            { label: 'Inception', value: date(fund.inceptionDate) },
            {
              label: 'Maturity',
              value: fund.maturityDate ? date(fund.maturityDate) : 'Open-ended',
            },
            { label: 'Unit basis', value: BASIS_LABELS[fund.basis] },
            {
              label: 'Distribution frequency',
              value: fund.distributionFrequency.toLowerCase().replace('_', '-'),
            },
          ]}
        />
      </SectionCard>
      <SectionCard
        title="Payments"
        action={
          <Button size="small" onClick={comingSoon}>
            Edit
          </Button>
        }
      >
        <KeyValueGrid
          items={[
            { label: 'Applications account', value: `${fund.bank.accountName}` },
            {
              label: 'BSB / account',
              value: `${fund.bank.bsb} · ${maskAccount(fund.bank.accountNumber)}`,
            },
            { label: 'APCA user ID (ABA files)', value: fund.apcaUserId },
            { label: 'BPAY biller code', value: fund.bpayBillerCode },
            { label: 'Bank feed', value: 'Connected (daily, demo)' },
            { label: 'Payment release', value: 'ABA upload by approver; bank API in phase 2' },
          ]}
        />
      </SectionCard>
      <SectionCard title="Registry rules">
        <Stack spacing={0.5}>
          {[
            ['Withhold 47% where no TFN/ABN is quoted', true],
            ['Apply non-resident interest withholding (10%)', true],
            ['Round distributions down to the cent; residue to the fund', true],
            ['Hold payments to unverified bank accounts', true],
            ['Allow investor-initiated redemptions', fund.basis === 'NAV_UNITS'],
            ['Allow off-market transfers (with approval)', true],
            ['Offer distribution reinvestment (DRP)', fund.basis === 'NAV_UNITS'],
          ].map(([label, checked]) => (
            <FormControlLabel
              key={String(label)}
              control={
                <Switch size="small" defaultChecked={Boolean(checked)} onChange={comingSoon} />
              }
              label={<Typography variant="body2">{label}</Typography>}
            />
          ))}
        </Stack>
      </SectionCard>
      <SectionCard title="Registry status">
        <KeyValueGrid
          items={[
            { label: 'Native register live', value: date(fund.registryLiveDate) },
            { label: 'Migrated from', value: fund.migratedFrom ?? '—' },
            { label: 'Feature flag', value: 'registry.enabled = true (this fund)' },
            {
              label: 'Fund Opportunity link',
              value: `Linked to fund opportunity ${fund.code} (marketing content)`,
            },
            {
              label: 'Previous registry',
              value: fund.migratedFrom ? 'Read-only until FY2026 statements archived' : '—',
            },
          ]}
        />
      </SectionCard>
    </Box>
  );
}
